import { Empty } from 'antd';
import LoggedScreen from 'components/BusinessLogic/ScreenTemplates/LoggedScreen';
import NewFilter from 'components/UI/NewFilter';
import { offerTypeMap } from 'enums/offerTypeMap';
import useIsElementVisible from 'hooks/useIsElementVisible';
import { useBreakpoints } from 'hooks/useMediaQuery';
import ProductOffer from 'model/ProductOffer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';
import ProductItem from 'screens/Products/components/ProductItem';
import { verifyIfChanged } from 'screens/Products/index';
import { ModeType as ProductsModeType } from 'screens/Products/utils/enums/ModeType.enum';
import { getModeListProductList } from 'services/config';
import {
  useGetMyOffersByIdQuery,
  useGetMyOffersCountQuery,
  useGetMyOffersQuery,
} from 'services/ecommerceApi';
import { getTypeMenu } from 'store/state/offerFilters/offerFiltersSelectors';
import { offerFiltersSelectors, sessionSelectors } from 'store/state/selectors';
import { ModeType } from './enums/ModeType.enum';

import * as S from './styles';

interface Props {
  product?: ProductOffer;
  openImage?(product: ProductOffer): void;
}

export default function MyOffers({ product, openImage }: Props) {
  const { isDesktop } = useBreakpoints();
  const lastRef = useRef<HTMLDivElement>(null);
  const [enablePagination, setEnablePagination] = useState<boolean>(true);
  const [products, setProducts] = useState<ProductOffer[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(10);
  const [offerIdInserted, setOfferIdInserted] = useState<number>();

  const {
    selectedCustomerId: customerId,
    deliveryDate,
    billingDate,
    sitesIds,
  } = useSelector(sessionSelectors.getRoot);

  const typeMenu = useSelector(getTypeMenu);

  const { data: countMyOffers, refetch: refetchMyOffers } =
    useGetMyOffersCountQuery(
      { deliveryDate, customerId },
      { skip: !customerId },
    );

  const offerFilters = useSelector(offerFiltersSelectors.getOfferFilters);
  const isLastVisible = useIsElementVisible(lastRef.current);

  useMemo(() => {
    setPage(1);
    setTotalPage(10);
    setEnablePagination(true);
  }, [offerFilters]);

  useMemo(() => {
    if (
      isLastVisible &&
      products !== null &&
      products &&
      products?.length > 0 &&
      enablePagination
    ) {
      const pageValue = page + 1;
      const totalPageValue = 10;
      setPage(pageValue);
      setTotalPage(totalPageValue);
      setEnablePagination(false);
    }
  }, [isLastVisible]);

  const [filterParams, setFilterParams] = useState<any>({
    ...offerFilters,
  });

  const { data } = useGetMyOffersQuery({
    isMyOffers: true,
    businessDate: billingDate,
    deliveryDate,
    page,
    siteId: sitesIds,
    totalPage,
    customerId,
    ...filterParams,
  });

  const { data: offersInserted } = useGetMyOffersByIdQuery(
    {
      isMyOffers: true,
      businessDate: billingDate,
      siteId: sitesIds,
      deliveryDate,
      page,
      totalPage,
      customerId,
      offerId: offerIdInserted,
    },
    {
      skip: !offerIdInserted,
    },
  );

  const resetList = useCallback(() => {
    setProducts([]);
  }, []);

  // Effect to catch store change - Offer filters
  useEffect(() => {
    setFilterParams((oldValue: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { deliveryDate, businessDate, ...rest } = offerFilters;
      if (verifyIfChanged(rest, oldValue)) {
        resetList();
      }
      return {
        ...oldValue,
        page: 1,
        ...rest,
      };
    });
  }, [offerFilters, resetList]);

  useEffect(() => {
    if (data) {
      const offers = data?.offers as ProductOffer[];
      if (offers?.length) {
        const dataKeys = products.map((product) => product.offerId);
        const productKeys = offers.map((item) => item.offerId);
        const isNewList = !dataKeys.some((r) => productKeys.includes(r));

        if (isNewList) {
          setEnablePagination(true);
          setProducts((oldValue) => [...oldValue, ...offers]);
        } else {
          setProducts(offers);
        }
      }
    }
  }, [data, products]);

  const [modeList, setModeList] = useState<ModeType>(2);


  const handleOpenImage = useCallback(() => {
    if (openImage && product) {
      openImage(product);
    }
  }, [product, openImage]);

  const handleOfferInserted = useCallback(
    (offerId: number, _offerType: offerTypeMap) => {
      setOfferIdInserted(offerId);
    },
    [],
  );

  const getProductItemMode = useCallback((): ProductsModeType => {
    if (modeList === 0) return ProductsModeType.Grid;
    if (modeList === 1) return ProductsModeType.List;
    return ProductsModeType.MinifyList;
  }, [modeList]);

  useEffect(() => {
    const offerInserted = offersInserted ? offersInserted.offers[0] : null;
    if (offerInserted) {
      setProducts((oldValue) =>
        oldValue.map((product) => {
          if (product.offerId === offerInserted.offerId) {
            return {
              ...offerInserted,
            };
          }
          return {
            ...product,
          };
        }),
      );
    }
  }, [offersInserted]);

  useEffect(() => {
    if (products && products?.length) {
      if (countMyOffers && countMyOffers > 0 && customerId) {
        refetchMyOffers();
      }
    }
  }, [countMyOffers, products, refetchMyOffers, customerId]);

  useEffect(() => {
    const mode = getModeListProductList();
    setModeList(mode);
  }, [typeMenu]);

  const newFilterDesktop = isDesktop && <NewFilter />;

  return (
    <LoggedScreen
      showListControls
      showSubHeader
      content={
        <S.Root>
          <S.Content
            isNewDesign
            className={products && products?.length ? '' : 'empty-data'}
          >
            {newFilterDesktop}
            <S.ProductCardsList isGrid={modeList === 0}>
              {products?.length ? (
                products.map((productOffer: ProductOffer) => (
                  <ProductItem
                    key={productOffer.offerId}
                    product={productOffer}
                    mode={getProductItemMode()}
                    runInMobile={isMobile}
                    openImage={handleOpenImage}
                    offerInserted={handleOfferInserted}
                    isExclusiveOffer
                  />
                ))
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </S.ProductCardsList>
          </S.Content>
          <div ref={lastRef} />
        </S.Root>
      }
    />
  );
}
